import React, { useEffect, useState } from 'react';
import { eventsApi, resultsApi } from '../services/api';
import ImageUploader from '../components/ImageUploader';

const EMPTY_EVENT = {
  name: '', name_it: '', name_en: '', name_de: '',
  description_it: '', description_en: '', description_de: '',
  date: '', time_start: '', city: '', address: '',
  latitude: '', longitude: '', category: 'corsa',
  fidal_event_id: '', image_url: '', website_url: '',
};

const CATEGORIES = [
  { value: 'corsa',         label: 'Corsa' },
  { value: 'maratona',      label: 'Maratona' },
  { value: 'mezza-maratona',label: 'Mezza maratona' },
  { value: 'atletismo',     label: 'Atletismo' },
  { value: 'trail',         label: 'Trail' },
  { value: 'triathlon',     label: 'Triathlon' },
  { value: 'indoor',        label: 'Indoor' },
  { value: 'cross',         label: 'Cross' },
];

export default function EventsPage({ admin }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [fidalSearch, setFidalSearch] = useState('');
  const [fidalResults, setFidalResults] = useState([]);
  const [scrapeStatus, setScrapeStatus] = useState({});

  const isSuperadmin = admin?.role === 'superadmin';

  const load = () =>
    eventsApi.list().then(r => setEvents(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await eventsApi.update(editing, form);
      } else {
        await eventsApi.create(form);
      }
      setShowForm(false); setEditing(null); setForm(EMPTY_EVENT);
      load();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare questo evento?')) return;
    try {
      await eventsApi.delete(id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore durante l\'eliminazione');
    }
  };

  const startEdit = (ev) => {
    setForm({ ...EMPTY_EVENT, ...ev });
    setEditing(ev.id);
    setShowForm(true);
  };

  const scrape = async (id) => {
    setScrapeStatus(s => ({ ...s, [id]: 'loading' }));
    try {
      const res = await resultsApi.scrape(id);
      setScrapeStatus(s => ({ ...s, [id]: `✅ ${res.data.scraped} risultati` }));
    } catch (err) {
      setScrapeStatus(s => ({ ...s, [id]: '❌ ' + (err.response?.data?.error || err.message) }));
    }
  };

  const searchFidal = async () => {
    try {
      const res = await resultsApi.searchFidal(fidalSearch);
      setFidalResults(res.data);
    } catch { alert('Errore nella ricerca FIDAL'); }
  };

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>📅 Eventi</h2>
          <p style={styles.subtitle}>
            {isSuperadmin
              ? `${events.length} eventi totali`
              : `${events.length} eventi assegnati a te`
            }
          </p>
        </div>
        {isSuperadmin && (
          <button style={styles.btnPrimary} onClick={() => { setForm(EMPTY_EVENT); setEditing(null); setShowForm(true); }}>
            + Nuovo evento
          </button>
        )}
      </div>

      {/* FIDAL search — superadmin only */}
      {isSuperadmin && (
        <div style={styles.fidalBox}>
          <div style={styles.fidalTitle}>🔍 Cerca su FIDAL per collegare l'ID</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              style={styles.input}
              value={fidalSearch}
              onChange={e => setFidalSearch(e.target.value)}
              placeholder="Nome dell'evento..."
              onKeyDown={e => e.key === 'Enter' && searchFidal()}
            />
            <button style={styles.btnSecondary} onClick={searchFidal}>Cerca</button>
          </div>
          {fidalResults.map((r, i) => (
            <div key={i} style={styles.fidalRow}>
              <span style={{ fontSize: 13 }}>{r.date} — <strong>{r.name}</strong> — {r.city}</span>
              {r.fidal_id && (
                <button style={styles.btnSmall} onClick={() =>
                  setForm(p => ({ ...p, fidal_event_id: r.fidal_id, name: r.name, city: r.city, date: r.date }))
                }>
                  Usa ID: {r.fidal_id}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <form onSubmit={save} style={styles.formCard}>
          <h3 style={styles.formTitle}>{editing ? '✏️ Modifica evento' : '➕ Nuovo evento'}</h3>

          <ImageUploader
            label="Immagine dell'evento"
            value={form.image_url}
            onChange={url => setForm(p => ({ ...p, image_url: url }))}
          />

          <div style={styles.grid2}>
            <label style={styles.label}>Nome (generale) *
              <input style={styles.input} value={form.name} onChange={f('name')} required />
            </label>
            <label style={styles.label}>Nome 🇮🇹 Italiano
              <input style={styles.input} value={form.name_it} onChange={f('name_it')} />
            </label>
            <label style={styles.label}>Nome 🇬🇧 Inglese
              <input style={styles.input} value={form.name_en} onChange={f('name_en')} />
            </label>
            <label style={styles.label}>Nome 🇩🇪 Tedesco
              <input style={styles.input} value={form.name_de} onChange={f('name_de')} />
            </label>

            {/* Structural fields — superadmin only */}
            {isSuperadmin && <>
              <label style={styles.label}>Data *
                <input style={styles.input} type="date" value={form.date} onChange={f('date')} required />
              </label>
              <label style={styles.label}>Orario inizio
                <input style={styles.input} type="time" value={form.time_start} onChange={f('time_start')} />
              </label>
              <label style={styles.label}>Città *
                <input style={styles.input} value={form.city} onChange={f('city')} required />
              </label>
              <label style={styles.label}>Indirizzo
                <input style={styles.input} value={form.address} onChange={f('address')} />
              </label>
              <label style={styles.label}>Latitudine
                <input style={styles.input} type="number" step="any" value={form.latitude} onChange={f('latitude')} placeholder="es. 41.8933" />
              </label>
              <label style={styles.label}>Longitudine
                <input style={styles.input} type="number" step="any" value={form.longitude} onChange={f('longitude')} placeholder="es. 12.4921" />
              </label>
              <label style={styles.label}>Categoria
                <select style={styles.input} value={form.category} onChange={f('category')}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label style={styles.label}>ID FIDAL
                <input style={styles.input} value={form.fidal_event_id} onChange={f('fidal_event_id')} placeholder="es. 12345" />
              </label>
              <label style={styles.label}>Sito web
                <input style={styles.input} value={form.website_url} onChange={f('website_url')} placeholder="https://..." />
              </label>
            </>}
          </div>

          <label style={styles.label}>Descrizione 🇮🇹 Italiano
            <textarea style={styles.textarea} value={form.description_it} onChange={f('description_it')} placeholder="Descrizione in italiano..." />
          </label>
          <label style={styles.label}>Descrizione 🇬🇧 Inglese
            <textarea style={styles.textarea} value={form.description_en} onChange={f('description_en')} placeholder="Description in English..." />
          </label>
          <label style={styles.label}>Descrizione 🇩🇪 Tedesco
            <textarea style={styles.textarea} value={form.description_de} onChange={f('description_de')} placeholder="Beschreibung auf Deutsch..." />
          </label>

          {!isSuperadmin && (
            <div style={styles.organizerNotice}>
              ℹ️ Come organizzatore puoi modificare solo i nomi, le descrizioni e l'immagine di questo evento.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button style={styles.btnPrimary} type="submit">💾 Salva</button>
            <button style={styles.btnSecondary} type="button" onClick={() => setShowForm(false)}>Annulla</button>
          </div>
        </form>
      )}

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Nome', 'Data', 'Città', 'Categoria', 'ID FIDAL', 'Azioni'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {events.map(ev => (
              <tr key={ev.id} style={styles.tr}>
                <td style={styles.td}>
                  <div style={{ fontWeight: 600, color: '#0D3B6E' }}>{ev.name}</div>
                  {ev.city && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>📍 {ev.city}</div>}
                </td>
                <td style={styles.td}>{ev.date}</td>
                <td style={styles.td}>{ev.city}</td>
                <td style={styles.td}>
                  <span style={styles.catBadge}>{ev.category}</span>
                </td>
                <td style={styles.td}>{ev.fidal_event_id || '—'}</td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button style={styles.btnSmall} onClick={() => startEdit(ev)} title="Modifica">✏️</button>
                    {isSuperadmin && (
                      <button style={styles.btnSmall} onClick={() => del(ev.id)} title="Elimina">🗑️</button>
                    )}
                    {isSuperadmin && ev.fidal_event_id && (
                      <button style={styles.btnSmall} onClick={() => scrape(ev.id)} title="Scarica risultati FIDAL">
                        {scrapeStatus[ev.id] === 'loading' ? '⏳' : '⬇️ FIDAL'}
                      </button>
                    )}
                    {scrapeStatus[ev.id] && scrapeStatus[ev.id] !== 'loading' && (
                      <span style={{ fontSize: 11, color: '#666' }}>{scrapeStatus[ev.id]}</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#aaa', padding: 30 }}>
                {isSuperadmin ? 'Nessun evento. Creane uno!' : 'Nessun evento assegnato. Contatta il superadmin.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  page:     { padding: 24 },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:    { margin: 0, color: '#0D3B6E', fontSize: 22 },
  subtitle: { margin: '4px 0 0', color: '#888', fontSize: 13 },

  fidalBox:  { background: '#EFF8FF', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #BFDBFE' },
  fidalTitle:{ fontWeight: 700, color: '#1D4ED8', fontSize: 14, marginBottom: 2 },
  fidalRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '7px 0', borderBottom: '1px solid #E0EEFE',
  },

  formCard:  { background: '#fff', borderRadius: 14, padding: 24, marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  formTitle: { margin: '0 0 16px', color: '#0D3B6E', fontSize: 17 },
  grid2:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  label:     { display: 'block', fontSize: 12, fontWeight: 700, color: '#555', letterSpacing: 0.3, marginBottom: 12 },
  input: {
    display: 'block', width: '100%', padding: '9px 11px',
    borderRadius: 8, border: '1.5px solid #ddd',
    marginTop: 5, fontSize: 14, boxSizing: 'border-box', color: '#333',
  },
  textarea: {
    display: 'block', width: '100%', padding: '9px 11px',
    borderRadius: 8, border: '1.5px solid #ddd',
    marginTop: 5, fontSize: 14, minHeight: 80,
    resize: 'vertical', boxSizing: 'border-box', marginBottom: 12,
  },
  organizerNotice: {
    background: '#FFF8F0', border: '1px solid #FDDCB5',
    borderRadius: 8, padding: '10px 14px',
    color: '#92400e', fontSize: 13, marginTop: 8,
  },

  tableWrap: { background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { background: '#0D3B6E', color: '#fff', padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 },
  td:        { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 13 },
  tr:        {},
  catBadge:  { background: '#EEF2FF', color: '#4C6FFF', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700 },

  btnPrimary:   { padding: '9px 18px', background: '#F26522', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  btnSecondary: { padding: '9px 18px', background: '#eee', color: '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  btnSmall:     { padding: '5px 11px', background: '#f0f2f5', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
};
